import { Controller } from "@hotwired/stimulus"
import { isExclusive, mediaKind, placementLabel, publishedPlacements } from "../lib/toolbox_placement"

/**
 * Toolbox panel controller — "à la Canva" left sidebar.
 *
 * All pages (catalogues + overlay editing) live in a single
 * <turbo-frame id="toolbox-content"> — navigation is server-driven. The home (list of tabs)
 * lives outside of it and is never unmounted.
 *
 * This controller manages:
 * - La vue courante, `data-toolbox-view` (home | page) : le CSS en déduit ce qui s'affiche,
 *   et le glissé d'une vue à l'autre (absente au chargement : la home, sans glisser)
 * - Le tiroir ouvert, `data-toolbox-open` : seul le mobile s'en sert (le CSS en déduit le
 *   tiroir, son fond de fermeture et le bouton de la barre mobile), le desktop l'affiche
 *   toujours. Une action de l'utilisateur vers une page de la toolbox (lien de la barre
 *   d'actions, `toolbox:show` avec une url) l'ouvre ; une sélection sur le canvas ou un
 *   rechargement du frame, non
 * - Mode state (used by toolbox-item outlet)
 * - Overlay panel deselect on home navigation
 * - La cible des médias (lib toolbox_placement) : l'éditeur la publie, l'en-tête des pages
 *   médias l'affiche, et un upload la fige à son dépôt pour s'y poser à l'arrivée
 */
export default class extends Controller {
  static targets = ["frame", "placement"]

  connect() {
    this._isOverlay = false
    this._placements = publishedPlacements()
    // Les lots d'upload en cours : la cible figée au dépôt, et si une cible exclusive (fond,
    // remplacement) a déjà reçu son média — d'un lot, seul le premier s'y pose.
    this._batches = new Map()
    // Sur mobile, le tiroir couvre le canvas : il se replie dès qu'on en tire un média.
    this._onDragStart = (e) => {
      if (!this._isOpen() || this._isDesktop()) return

      const img = e.target.closest("[draggable]")?.querySelector("img")
      if (img && e.dataTransfer) {
        const ghost = new Image()
        ghost.src = img.src
        ghost.style.cssText = "position:absolute;top:-9999px;width:80px;height:auto"
        document.body.appendChild(ghost)
        e.dataTransfer.setDragImage(ghost, 40, 40)
        setTimeout(() => ghost.remove(), 0)
      }

      this.close()
    }
    this.element.addEventListener("dragstart", this._onDragStart)
  }

  disconnect() {
    this.element.removeEventListener("dragstart", this._onDragStart)
  }

  open() {
    this.element.toggleAttribute("data-toolbox-open", true)
  }

  close() {
    this.element.toggleAttribute("data-toolbox-open", false)
  }

  toggle() {
    if (this._isOpen()) this.close()
    else this.open()
  }

  /** Un lien vers une page de la toolbox (barre d'actions…) : le tiroir s'ouvre avec elle. */
  openForLink(event) {
    if (event.target.closest('[data-turbo-frame="toolbox-content"]')) this._openOnto()
  }

  /** Show a panel from a dispatched event (e.g. from floating toolbar or fabric controller).
   *  detail.url (optionnelle) : la page à charger dans le frame — le panneau d'une séquence
   *  vidéo voyage ainsi, ses params (fond, couleur) sont un état purement client — et le
   *  tiroir s'ouvre avec elle.
   *  detail.root : retour à la racine du panneau sans quitter l'objet (la barre d'actions
   *  ferme la sous-page de son outil). */
  showFromEvent({ detail: { key, url, root } }) {
    if (url) this._navigateTo(url)
    else if (root) this._showHome()

    // Leaving overlay → deselect the object on the canvas
    if (this._isOverlay && key !== "overlay") {
      window.dispatchEvent(new CustomEvent("overlay-panel:deselect"))
      this._isOverlay = false
    }

    if (key === "home") {
      this._navigateHome()
    } else if (key === "overlay") {
      this._isOverlay = true
    }

    if (url) this._openOnto()
  }

  // La page courante de l'éditeur (`page:changed`) : ce qui n'a de sens que sur une page
  // répétée (`[data-requires-repeat]`) ne se montre que là — rejoué à chaque chargement du
  // frame, la toolbox arrivant après l'annonce.
  pageChanged(event) {
    this.pageRepeat = Boolean(event.detail?.repeat);
    this.applyPageContext();
  }

  applyPageContext() {
    // Toute la toolbox : la page du frame comme les aperçus de la home.
    this.element.querySelectorAll("[data-requires-repeat]").forEach((el) => { el.hidden = !this.pageRepeat; });
  }

  // ==================== La cible des médias ====================

  /** L'éditeur a publié sa cible (`toolbox:placement`) : l'en-tête la suit. */
  placementChanged({ detail }) {
    this._placements = detail
    this.placementTargets.forEach((el) => this._renderPlacement(el))
  }

  placementTargetConnected(el) {
    this._renderPlacement(el)
  }

  /** Un lot déposé dans un uploader de la toolbox : sa cible est celle de cet instant. */
  freezePlacement({ detail: { batch } }) {
    this._batches.set(batch, { placements: this._placements, exclusiveTaken: false })
  }

  /** Un média uploadé arrive dans la grille : il est posé sur la cible figée de son lot. */
  placeUploaded(event) {
    const { batch, item } = event.detail
    const frozen = this._batches.get(batch)
    const placement = frozen?.placements?.[mediaKind(item)]
    if (!placement) return

    if (isExclusive(placement)) {
      if (frozen.exclusiveTaken) return
      frozen.exclusiveTaken = true
    }
    event.target.dispatchEvent(new CustomEvent("toolbox-item:selected", {
      detail: { ...item, placement },
      bubbles: true,
    }))
  }

  /** Le titre de la page dit la cible de sa famille de média (`data-kind`), à défaut le
   *  sien (`data-title`). */
  _renderPlacement(el) {
    el.textContent = placementLabel(this._placements?.[el.dataset.kind] ?? null) || el.dataset.title
  }

  // Les sous-pages se rechargent en morph après chaque sauvegarde — y compris celle que la
  // frappe en cours vient de déclencher. Le champ qui a le focus garde ce qu'on y tape :
  // le morph ne lui réécrit ni sa valeur ni ses attributs (Turbo ne pose pas
  // ignoreActiveValue). Au blur, le prochain rechargement repart du document.
  keepTyping(event) {
    if (event.target === document.activeElement) event.preventDefault();
  }

  /** La vue courante et le tiroir ouvert sont un état client : un morph de la page (le
   *  serveur rend la home, tiroir fermé) n'y touche pas. */
  keepView(event) {
    if (event.target !== this.element) return
    if (["data-toolbox-view", "data-toolbox-open"].includes(event.detail.attributeName)) event.preventDefault()
  }

  /** Une sous-page a chargé dans le frame (et pas un frame imbriqué dans la sous-page). */
  showPage(event) {
    if (event.target !== this.frameTarget) return
    this.element.dataset.toolboxView = "page"
    delete this.element.dataset.toolboxPending
  }

  // ==================== Private ====================

  _navigateHome() {
    if (this._isOverlay) {
      window.dispatchEvent(new CustomEvent("overlay-panel:deselect"))
      this._isOverlay = false
    }
    this._showHome()
  }

  /** Retour à la home : rien à recharger, elle est restée dans le DOM. Le frame est vidé —
   *  la page quittée ne garde pas ses contrôleurs branchés, ni de src à recharger. */
  _showHome() {
    this.element.dataset.toolboxView = "home"
    delete this.element.dataset.toolboxPending
    if (!this.hasFrameTarget) return
    this.frameTarget.removeAttribute("src")
    this.frameTarget.replaceChildren()
  }

  _navigateTo(url) {
    if (this.hasFrameTarget) this.frameTarget.src = url
  }

  _isDesktop() {
    return window.matchMedia("(min-width: 768px)").matches
  }

  /** Le tiroir s'ouvre sur une page demandée : fermé, il montrait encore la précédente —
   *  le frame reste masqué (data-toolbox-pending) jusqu'à l'arrivée de la nouvelle. */
  _openOnto() {
    if (!this._isOpen()) this.element.dataset.toolboxPending = ""
    this.open()
  }

  _isOpen() {
    return this.element.hasAttribute("data-toolbox-open")
  }
}
